/* eslint-disable quote-props */
import { IPoiOpening } from "~/interfaces";
import { dateFormat } from "~/util/format";
import { IReview, IReviewItem } from "./interfaces";
import { node } from "~/util/dom";
import { IDetailState } from "~/state/detail/interfaces";

function buildDay(interval: Array<[number, number][]>) {
	if (!interval || !interval.length) {
		return "nonstop";
	}

	return interval.map(inter => inter.map(time => time.map((num, ind) => ind === 0 ? num : num.toString().padStart(2, "0")).join(":")).join("-")).join(", ");
}

function getOpeningHours(data: IPoiOpening): Array<string> {
	const opening = [];

	if (data) {
		const date = new Date();
		const curDay = date.getDate();
		const curMonth = (date.getMonth() + 1) * 100;
		const curDate = curMonth + curDay;
		const seasons = data.seasons || [];
		let selSeason = null;

		for (let ind = 0, max = seasons.length; ind < max; ind++) {
			const season = seasons[ind];

			if (
				(season.end > season.start && curDate >= season.start && curDate <= season.end)
				|| ((season.end < season.start) && ((curDate >= 101 && curDate <= season.end) || (curDate >= season.start && curDate <= 1231)))
			) {
				selSeason = season;
				break;
			}
		}

		if (selSeason) {
			const daysAbbr = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

			for (let ind = 1; ind < 8; ind++) {
				const day = selSeason.days[ind];

				opening.push(`${daysAbbr[ind % 7]} ${day ? buildDay(day.interval) : "closed"}`);
			}
		}
	}

	return opening;
}

interface ILDJSONReview {
	"@type": "Review";
	author: {
		type: string;
		name: string;
	};
	datePublished: string;
	reviewBody: string;
	reviewRating: {
		"@type": "Rating";
		bestRating: string;
		ratingValue: string;
		worstRating: string;
	},
}

interface ILDJSONAggregateRating {
	"@type": "AggregateRating";
	ratingCount: string;
	ratingValue: string;
	worstRating: string;
	bestRating: string;
}

interface ILDJSONCard {
	"@context": "http://schema.org";
	"@type": "LocalBusiness";
	name: string;
	description: string;
	openingHours: Array<string>;
	image: string;
	geo: {
		"@type": "Geocoordinates";
		latitude: number;
		longitude: number;
	};
	address: string;
	url: string;
	telephone: string;
	email: string;
	aggregateRating?: ILDJSONAggregateRating;
	review?: Array<ILDJSONReview>;
}

export function buildSeoCard(detailData: IDetailState, firmData: IReview, reviewsData: Array<IReviewItem>) {
	const www = detailData.data.extend.contact?.www;
	const phone = detailData.data.extend.contact?.phone;
	const email = detailData.data.extend.contact?.email;
	const detailGallery = Mapy.getComponent("detail").getGalleryData();
	const openingHours = getOpeningHours(detailData.data.extend.opening);
	const card: ILDJSONCard = {
		"@context": "http://schema.org",
		"@type": "LocalBusiness",
		name: detailData.data.title,
		description: detailData.data.description,
		openingHours,
		image: detailGallery.best ? `https:${detailGallery.best?.urls.default}`.replace(/[?].*$/u, "") : "",
		geo: {
			"@type": "Geocoordinates",
			latitude: detailData.mark.lat,
			longitude: detailData.mark.lon,
		},
		address: detailData.data.titleVars?.locationMain3?.length ? detailData.data.titleVars.locationMain3.join(", ") : "",
		url: www?.value.length ? www.value[0].url : "",
		telephone: phone?.value.length ? phone.value[0].countrycode?.replace(/[&][^;]+;/ug, "") || "" : "",
		email: email?.value.length ? email.value[0] : "",
	};

	if (firmData.total > 0) {
		card.aggregateRating = {
			"@type": "AggregateRating",
			ratingCount: firmData.total.toString(),
			ratingValue: firmData.review_rating_stars.toFixed(1),
			worstRating: "0",
			bestRating: "5",
		};
	}

	if (reviewsData.length) {
		card.review = reviewsData.map(item => ({
			"@type": "Review",
			author: {
				type: "person",
				name: item.user?.name || "anonym",
			},
			datePublished: dateFormat(new Date(item.updated), "yyyy-mm-dd"),
			reviewBody: item.text,
			reviewRating: {
				"@type": "Rating",
				bestRating: "5",
				ratingValue: (item.rating / 100 * 5).toFixed(1),
				worstRating: "1",
			},
		}));
	}

	const scriptElement = node("script", { type: "application/ld+json" });

	scriptElement.textContent = JSON.stringify(card);

	return scriptElement;
}
